import LegalLayout, { LegalSection, LegalList } from "../../components/chalk/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout title="Terms of Service" eyebrow="Legal" lastUpdated="February 2026" testId="page-terms">
      <LegalSection heading="About Chalk OS">
        <p>
          Chalk OS is a youth sports coordination platform. By creating an account, posting content,
          managing a team, or otherwise using Chalk OS, you agree to these Terms of Service. If you do
          not agree, do not use Chalk OS.
        </p>
      </LegalSection>

      <LegalSection heading="What Chalk OS is — and is not">
        <p>
          Chalk OS is <strong>not</strong> a school, league governing body, emergency service, medical
          provider, legal advisor, transportation provider, payment guarantor, or background screening
          service. Chalk OS does not supervise children, provide childcare, verify adult eligibility,
          conduct background checks, or guarantee the conduct of any coach, volunteer, parent, guardian,
          athlete, official, league, or third party.
        </p>
      </LegalSection>

      <LegalSection heading="Coach, league admin, and guardian responsibilities">
        <p>
          Coaches, league admins, and guardians are responsible for verifying schedules, locations,
          athlete information, emergency contacts, medical notes, transportation plans, and all team
          decisions. Chalk OS surfaces and routes information you provide; it does not validate the
          accuracy or completeness of that information.
        </p>
      </LegalSection>

      <LegalSection heading="Account eligibility">
        <LegalList items={[
          "You must be at least 18 years old to create an independent Chalk OS account.",
          "Children do not create independent Chalk OS accounts in V1.",
          "Athlete profiles are created and managed by coaches, league admins, or guardians.",
          "You agree to provide accurate information and to keep your account credentials secure.",
        ]} />
      </LegalSection>

      <LegalSection heading="Emergency communication">
        <p>
          Emergency communications may be delayed by device settings, internet access, carrier issues,
          email filters, app permissions, or third-party service outages. Chalk OS should not be used
          as the only method for emergency communication.
        </p>
      </LegalSection>

      <LegalSection heading="AI features">
        <p>
          AI features, including Draw It Up and Erase and Redraw drafts, provide writing assistance
          only. AI output may be incomplete or inaccurate. Coaches must review all AI-generated content
          before sending it.
        </p>
      </LegalSection>

      <LegalSection heading="Subscriptions and billing">
        <p>
          Paid plans renew automatically until cancelled. Subscription fees are non-refundable except
          where required by law. See our <a href="/refund-policy" style={{ color: "#2E7D52" }}>Refund Policy</a> for details.
        </p>
      </LegalSection>

      <LegalSection heading="Tournament billing terms" id="tournament-billing">
        <div data-testid="terms-tournament-billing" id="tournament-billing">
        <LegalList items={[
          "The Tournament Hub is priced per event by the number of teams that check in or play at least one game: up to 16 teams $69, 17–32 teams $99, 33–64 teams $179. 65 or more teams is a custom plan; contact us.",
          "At upgrade we charge the band that matches the teams registered at that moment and save your card with Stripe for any post-event adjustment.",
          "After the event (when it is marked complete, or 24 hours after its end date) we count the final teams. If the final band is higher, we email you the difference and charge the saved card 48 hours later. If it is lower, we refund the difference automatically.",
          "Extra helpers on a per-event Hub tournament are $5 per helper beyond the 3 included, added to the post-event bill.",
          "Your first Hub event is free for up to 32 teams (one per account and per card; a card on file is required). If it finishes with 33–64 teams, the $80 difference is charged after the event.",
          "Director ($299/year) includes up to 6 Hub events per plan year with up to 32 teams each; Director Plus ($499/year) includes unlimited Hub events with up to 64 teams each. An event counts when it goes live (bracket built or first check-in). A Director event that finishes with 33–64 teams is charged the $80 band difference. Each plan includes 5 helpers per event; extra helper seats are $25 per helper per year.",
          "Annual plans renew automatically until canceled. Canceling takes effect at the end of the paid year. If a renewal payment fails you keep full access for 14 days while we retry.",
          "If a post-event charge fails, live and past events keep working, but you can't upgrade new events until the balance is paid.",
          "By checking the authorization box at checkout you authorize ChalkOS to charge your saved card after the event for the final team count and any extra helpers, as described here. Charges appear as CHALKOS on your statement. Sales tax may be added where required.",
        ]} />
        </div>
      </LegalSection>

      <LegalSection heading="Content and conduct">
        <p>
          You retain ownership of content you post. You grant Chalk OS a limited license to host,
          transmit, and display that content as necessary to operate the platform. Misuse of Chalk OS,
          including harassment, impersonation, or unsafe content, is governed by our{" "}
          <a href="/community-policy" style={{ color: "#2E7D52" }}>Community Policy</a> and may result in
          content removal, account restriction, or suspension.
        </p>
      </LegalSection>

      <LegalSection heading="Disclaimer of warranties; limitation of liability">
        <p>
          Chalk OS is provided "as is" without warranties of any kind. To the maximum extent permitted
          by law, Chalk OS, its operators, and its providers are not liable for indirect, incidental,
          consequential, special, or exemplary damages arising from your use of the platform. Our
          aggregate liability for any claim is limited to the amount you paid Chalk OS in the twelve
          months preceding the event giving rise to the claim, or one hundred US dollars, whichever is greater.
        </p>
      </LegalSection>

      <LegalSection heading="Changes to these terms">
        <p>
          We may update these terms from time to time. If we make material changes, we will update the
          "Last updated" date and notify account holders through the app or email.
        </p>
      </LegalSection>

      <LegalSection heading="Contact">
        <p>
          For legal notices or questions about these terms, email{" "}
          <a href="mailto:support@getchalkos.com" style={{ color: "#2E7D52" }}>support@getchalkos.com</a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
